// Postausgang › Schreiben (#586): Versandläufe des Schreiben-Assistenten – je Lauf Empfänger mit Weg und Stand,
// „Fortsetzen“ für alles, was nicht (vollständig) rausging.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowClockwise, CaretDown, CaretRight, CircleNotch } from '@phosphor-icons/react';
import { ImmoButton } from '../../../components/immobilien/ui';
import { ZUSTELLWEG_LABEL } from '../../../lib/schreiben/typen';
import { fuehreAus, standardDienste } from '../../../lib/schreiben/versand';
import { ladePdfGestaltungNachId } from '../../../lib/schreiben/gestaltung';
import { ladeEigeneEmailSignatur } from '../../../lib/schreiben/signatur';
import { anzeigeBetreff, wiedervorlageNachVersand } from '../../../lib/schreiben/wiedervorlage';
import { ladeLaeufe, ladeLaufZumFortsetzen, ladeStandardKonto, type SchreibenLauf } from '../../../lib/schreiben/protokoll';

const wegeText = (w: string[]) => w.map((x) => ZUSTELLWEG_LABEL[x as keyof typeof ZUSTELLWEG_LABEL] ?? x).join(' + ');
const STAND: Record<string, { label: string; farbe: string }> = {
  offen: { label: 'offen', farbe: 'var(--ink3)' }, laeuft: { label: 'läuft', farbe: 'var(--info)' }, gesendet: { label: 'gesendet', farbe: 'var(--ok)' },
  teilweise: { label: 'teilweise', farbe: 'var(--warn)' }, fehler: { label: 'Fehler', farbe: 'var(--bad)' },
};
const datum = (iso: string) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

export function SchreibenLaeufe() {
  const [laeufe, setLaeufe] = useState<SchreibenLauf[] | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const navigate = useNavigate();
  const laden = useCallback(() => void ladeLaeufe().then(setLaeufe), []);
  useEffect(laden, [laden]);

  const fortsetzen = async (id: string) => {
    setLaeuft(id); setMeldung(null);
    try {
      const [konto, lauf, signatur] = await Promise.all([ladeStandardKonto(), ladeLaufZumFortsetzen(id), ladeEigeneEmailSignatur().catch(() => null)]);
      const r = await fuehreAus(lauf, standardDienste(konto, lauf.schreibenId, await ladePdfGestaltungNachId(lauf.briefkopfId ?? null, lauf.absenderId ?? null), signatur?.html ?? null));
      // Etappe 4: gewünschte Wiedervorlage nachlegen, wenn sie beim ersten Senden nicht entstand
      let wv = '';
      if (lauf.wiedervorlage && !lauf.wiedervorlageTicketId && r.gesendet + r.teilweise > 0) {
        try {
          const t = await wiedervorlageNachVersand({ schreibenId: lauf.schreibenId, betreff: lauf.betreff, eingaben: lauf.eingaben, empfaenger: lauf.empfaenger.map((x) => x.e), wunsch: lauf.wiedervorlage });
          wv = ` Wiedervorlage ${t.ticket_nummer} angelegt.`;
        } catch (e) { wv = ` Wiedervorlage nicht angelegt: ${(e as Error).message}`; }
      }
      setMeldung(`Fortgesetzt: ${r.gesendet} gesendet${r.teilweise ? `, ${r.teilweise} teilweise` : ''}${r.fehler ? `, ${r.fehler} Fehler` : ''}.${wv}`);
      laden();
    } catch (e) { setMeldung((e as Error).message); } finally { setLaeuft(null); }
  };

  if (laeufe === null) return null;
  if (laeufe.length === 0) return <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Schreiben über den Assistenten verschickt.</span>;
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      {meldung && <span role="status" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)' }}>{meldung}</span>}
      {laeufe.map((l) => {
        const n = (s: string) => l.empfaenger.filter((e) => e.stand === s).length;
        const nachzuholen = l.empfaenger.length - n('gesendet');
        const auf = offen === l.id;
        return (
          <div key={l.id} className="flex flex-col" style={{ gap: 6, padding: '10px 12px', borderRadius: 14, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
            <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
              <button type="button" aria-expanded={auf} onClick={() => setOffen(auf ? null : l.id)} className="flex min-w-0 flex-1 items-center text-left cursor-pointer"
                style={{ gap: 6, border: 'none', background: 'transparent', color: 'var(--ink)', padding: 0, fontFamily: 'inherit' }}>
                {auf ? <CaretDown size={13} weight="bold" /> : <CaretRight size={13} weight="bold" />}
                <span className="truncate" style={{ fontSize: 13.5, fontWeight: 800 }}>{anzeigeBetreff(l.betreff ?? '', l.eingaben ?? {}, l.empfaenger.map((e) => e.werte ?? {}))}</span>
              </button>
              <span className="immo-zahl" style={{ fontSize: 12.5, color: 'var(--ink2)' }}>
                {l.empfaenger.length} Empfänger · {n('gesendet')} gesendet{n('teilweise') ? ` · ${n('teilweise')} teilweise` : ''}{n('fehler') ? ` · ${n('fehler')} Fehler` : ''}{n('offen') ? ` · ${n('offen')} offen` : ''} · {datum(l.created_at)}
              </span>
              {l.wiedervorlage && (
                <ImmoButton groesse={32} onClick={() => navigate(`/admin/tickets/${l.wiedervorlage!.id}`)}>Wiedervorlage {l.wiedervorlage.ticket_nummer}</ImmoButton>
              )}
              {nachzuholen > 0 && (
                <ImmoButton groesse={32} icon={laeuft === l.id ? CircleNotch : ArrowClockwise} onClick={() => void fortsetzen(l.id)} disabled={laeuft !== null}>Fortsetzen ({nachzuholen})</ImmoButton>
              )}
            </div>
            {auf && (
              <ul className="m-0 flex flex-col p-0" style={{ gap: 2, listStyle: 'none', paddingLeft: 18 }}>
                {l.empfaenger.map((e) => (
                  <li key={e.id} className="flex flex-col" style={{ fontSize: 12.5, padding: '4px 0', borderBottom: '1px solid var(--line)' }}>
                    <span><b>{e.name ?? '–'}</b> · {wegeText(e.wege ?? [])} · <span style={{ color: STAND[e.stand]?.farbe, fontWeight: 700 }}>{STAND[e.stand]?.label ?? e.stand}</span></span>
                    {e.fehler && <span style={{ color: 'var(--bad)' }}>{e.fehler}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
